import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QUICK_REACTIONS, EMOJI_GROUPS } from "./emojiData";

const PILL_H = 56;

// iMessage Tapback: dimmed, blurred backdrop; the pressed bubble lifts; a floating pill sits above it.
export const TapbackOverlay = ({ open, bubbleEl, align, current, onPick, onClose, isLight }) => {
  const [more, setMore] = useState(false);
  const [rect, setRect] = useState(null);
  const cloneRef = useRef(null);

  useEffect(() => {
    if (!open || !bubbleEl) return;
    setRect(bubbleEl.getBoundingClientRect());
    setMore(false);
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, bubbleEl, onClose]);

  const mountClone = (el) => {
    cloneRef.current = el;
    if (!el || !bubbleEl || !rect) return;
    el.innerHTML = "";
    const c = bubbleEl.cloneNode(true);
    c.style.maxWidth = "none";
    c.style.width = `${rect.width}px`;
    c.style.margin = "0";
    el.appendChild(c);
  };

  if (!open || !rect) return null;

  const vw = window.innerWidth;
  const above = rect.top > PILL_H + 24;
  const pillTop = above ? rect.top - PILL_H - 10 : rect.bottom + 10;
  const pillStyle = { top: pillTop };
  if (align === "right") pillStyle.right = Math.max(8, vw - rect.right);
  else pillStyle.left = Math.max(8, rect.left);

  const pillBg = isLight ? "bg-white/95 text-gray-900" : "bg-[#2c2c2e]/95 text-white";
  const sheetBg = isLight ? "bg-white text-gray-900" : "bg-[#1c1c1e] text-white";

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        key="tapback"
        className="fixed inset-0 z-[10000]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        data-testid="reaction-picker"
      >
        <button className="absolute inset-0 bg-black/30 backdrop-blur-[6px]" onClick={onClose} aria-label="Close" data-testid="reaction-picker-backdrop" />

        <motion.div
          ref={mountClone}
          className="fixed pointer-events-none"
          style={{ top: rect.top, left: rect.left, width: rect.width, transformOrigin: align === "right" ? "right center" : "left center" }}
          initial={{ scale: 1 }}
          animate={{ scale: 1.03 }}
          transition={{ type: "spring", stiffness: 400, damping: 28 }}
        />

        <motion.div
          className={`fixed flex items-center gap-0.5 px-2 rounded-full shadow-2xl ${pillBg}`}
          style={{ ...pillStyle, height: PILL_H, transformOrigin: `${above ? "bottom" : "top"} ${align === "right" ? "right" : "left"}` }}
          initial={{ scale: 0.5, opacity: 0, y: above ? 10 : -10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 520, damping: 30 }}
          data-testid="tapback-pill"
        >
          {QUICK_REACTIONS.map((e, i) => (
            <motion.button
              key={e}
              onClick={() => onPick(e)}
              className={`w-11 h-11 rounded-full flex items-center justify-center text-[26px] leading-none ${current === e ? "bg-[#0A84FF]" : ""}`}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 600, damping: 26, delay: 0.03 * i }}
              whileTap={{ scale: 0.85 }}
              whileHover={{ scale: 1.2 }}
              data-testid={`reaction-quick-${e}`}
            >
              {e}
            </motion.button>
          ))}
          <motion.button
            onClick={() => setMore(true)}
            className={`w-10 h-10 ml-0.5 rounded-full flex items-center justify-center ${isLight ? "bg-gray-200/80 text-gray-600" : "bg-white/15 text-white/80"}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 600, damping: 26, delay: 0.2 }}
            whileTap={{ scale: 0.85 }}
            aria-label="More emojis"
            data-testid="reaction-more-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><path d="M9 9h.01M15 9h.01" /></svg>
          </motion.button>
        </motion.div>

        <AnimatePresence>
          {more && (
            <motion.div
              className={`fixed left-0 right-0 bottom-0 max-h-[55vh] rounded-t-3xl shadow-2xl flex flex-col ${sheetBg}`}
              style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 420, damping: 36 }}
              data-testid="reaction-more-grid"
            >
              <div className="mx-auto mt-2 mb-1 w-10 h-1.5 rounded-full bg-gray-400/40" />
              <div className="overflow-y-auto px-4 pb-4">
                {EMOJI_GROUPS.map((g) => (
                  <div key={g.name}>
                    <p className={`text-[11px] font-semibold uppercase tracking-wide mt-3 mb-1 ${isLight ? "text-gray-400" : "text-white/40"}`}>{g.name}</p>
                    <div className="grid grid-cols-8 gap-1">
                      {g.emojis.map((e) => (
                        <button key={e} onClick={() => onPick(e)} className={`h-11 rounded-xl text-[26px] leading-none flex items-center justify-center active:scale-90 transition-transform ${current === e ? "bg-[#0A84FF]/25" : isLight ? "hover:bg-gray-100" : "hover:bg-white/10"}`} data-testid={`reaction-emoji-${e}`}>{e}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};
